'use client'

import './globals.css';
import { JetBrains_Mono } from 'next/font/google';
import { Analytics } from '@vercel/analytics/react';
import Link from 'next/link';
import { Waves } from '@/components/ui/waves-background';
import { Navigation } from '@/components/Navigation';
import { usePathname } from 'next/navigation';
import { ArrowLeft } from 'lucide-react';

const jetbrainsMono = JetBrains_Mono({ subsets: ['latin'] });

export default function RootLayout({
  children
}: Readonly<{
  children: React.ReactNode;
}>) {
  const pathname = usePathname();

  // Determine header content based on current path
  const getHeaderContent = () => {
    if (pathname === '/') {
      return {
        title: 'Elvan Konukseven',
        subtitle: 'CS student at ETHZ & Developer'
      };
    } else if (pathname === '/blog') {
      // Only show Blog header for the main blog page, not individual articles
      return {
        title: 'Blog',
        subtitle: 'Some articles on my experience as a junior dev.'
      };
    } else if (pathname.startsWith('/blog/')) {
      // Individual blog articles - no header, just back arrow
      return null;
    } else if (pathname.startsWith('/projects')) {
      return {
        title: 'Projects',
        subtitle: 'Some projects I have worked on'
      };
    } else {
      // Fallback for any other routes
      return {
        title: 'Elvan Konukseven',
        subtitle: 'Software developer passionate about open source'
      };
    }
  };

  const headerContent = getHeaderContent();
  const isArticlePage = pathname.startsWith('/blog/') && pathname !== '/blog';

  return (
    <html lang="en" className={`${jetbrainsMono.className}`}>
      <head>
        <title>Elvan Konukseven | CS student @ETHZ</title>
        <meta name="description" content="CS student passionate about open source, machine learning, and full-stack development. Explore my projects, blog posts, and GSoC journey." />
        <link rel="manifest" href="/manifest.json" />
        <meta name="theme-color" content="#111827" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/* Favicon files */}
        <link rel="icon" href="/favicon.ico" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      </head>
      <body className="antialiased tracking-tight text-gray-900">
        <div className="min-h-screen flex flex-col relative">
          {/* Interactive waves background - transparent and subtle */}
          <Waves 
            className="absolute inset-0 w-full h-full -z-10"
            lineColor="rgba(107, 114, 128, 0.15)"
            backgroundColor="transparent"
            waveSpeedX={0.01}
            waveSpeedY={0.005}
            waveAmpX={25}
            waveAmpY={15}
            xGap={15}
            yGap={40}
            friction={0.9}
            tension={0.003}
            maxCursorMove={80}
          />
          
          <div className="flex-1 py-16 px-4 md:py-24 md:px-8 relative z-10">
            <div className="max-w-4xl mx-auto">
              {/* Regular pages: show header with navigation */}
              {headerContent && (
                <>
                  {/* Desktop: name and nav side by side */}
                  <header className="hidden md:flex justify-between items-start mb-16">
                    <div className="flex-1 max-w-2xl">
                      <h1 className="text-3xl font-bold text-gray-900 mb-4">{headerContent.title}</h1>
                      {headerContent.subtitle && (
                        <p className="text-base text-gray-600">{headerContent.subtitle}</p>
                      )}
                    </div>
                    <div className="ml-8 min-w-[120px]">
                      <Navigation />
                    </div>
                  </header>
                  
                  {/* Mobile: name and subtitle */}
                  <div className="md:hidden mb-6">
                    <h1 className="text-lg font-bold text-gray-900 mb-2">{headerContent.title}</h1>
                    {headerContent.subtitle && (
                      <p className="text-base text-gray-600 mb-24">{headerContent.subtitle}</p>
                    )}
                  </div>
                </>
              )}
              
              {/* Article pages: just back arrow at top */}
              {isArticlePage && (
                <div className="mb-8">
                  <Link 
                    href="/blog" 
                    className="inline-flex items-center text-gray-600 hover:text-pink-500 transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Link>
                </div>
              )}
              
              <main className="max-w-2xl space-y-12 -mt-4 md:-mt-10">
                {children}
              </main>
              
              {/* Mobile navigation at bottom - only for non-article pages */}
              {!isArticlePage && (
                <div className="md:hidden mt-16 pt-8 border-t border-gray-200">
                  <Navigation />
                </div>
              )}
            </div>
          </div>

          <Analytics />
        </div>
      </body>
    </html>
  );
}
